import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  useTheme,
  useMediaQuery,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Alert,
  Tooltip,
  Stack
} from '@mui/material';
import {
  ArrowLeft,
  PencilSimple,
  Phone,
  Envelope,
  Calendar,
  MapPin,
  Tag,
  Clock,
  FileText,
  Receipt,
  Paperclip,
  Bell,
  Plus,
  Warning,
  Trash,
  CheckCircle,
  Download,
  Eye,
  X,
  Archive,
  Article
} from '@phosphor-icons/react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import api from '../../../utils/api';
import { useBusiness } from '../../../context/BusinessContext';
import NotesEditor from './NotesEditor';
import StatusBadge from '../Invoices/StatusBadge';
import EventForm from '../../UnifiedBooking/EventForm';
import SupplierFeatureGate from '../../Business/SupplierFeatureGate';
import useThemeStyles from '../../../theme/useThemeStyles';
import FormSubmissionEntryDialog from '../FormSubmissionEntryDialog';
import { DEFAULT_CRM_LEAD_SOURCES, mergeLeadSources, getLeadSourceLabel } from '../../../utils/crmLeadSources';
import { getBookingGuestDisplayName, getBookingGuestEmailLine } from '../../../utils/unifiedBookingDisplay';

// Helper functions for activity timeline
const getStageDisplayName = (stage) => {
  if (!stage) return 'Unknown';
  const label = stage.replace(/[_-]/g, ' ');
  return label.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
};

const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const normaliseTagValue = (tagValue) => {
  if (typeof tagValue === 'string') return tagValue.trim();
  if (!tagValue || typeof tagValue !== 'object') return String(tagValue || '').trim();
  if (typeof tagValue.label === 'string') return tagValue.label.trim();
  if (typeof tagValue.value === 'string') return tagValue.value.trim();
  if (typeof tagValue.name === 'string') return tagValue.name.trim();
  return '';
};

const _getActivityIcon = (type) => {
  switch (type) {
    case 'created': return '🎯';
    case 'stage_changed': return '🔄';
    case 'assigned': return '👤';
    case 'wedding_linked': return '💒';
    case 'note_added': return '📝';
    case 'file_uploaded': return '📎';
    case 'reminder_set': return '⏰';
    case 'follow_up_completed': return '✅';
    case 'manual_activity': return '📋';
    case 'event_created': return '📅';
    case 'questionnaire_sent': return '📧';
    case 'questionnaire_completed': return '✅';
    default: return '📋';
  }
};

const getActivityDescription = (activity) => {
  // If the description already looks good (contains proper stage names), use it
  if (activity.description && activity.description.includes('Stage changed from') && 
      activity.description.includes(' to ') && 
      !activity.description.includes('_')) {
    return activity.description;
  }
  
  // Otherwise, format it properly
  switch (activity.type) {
    case 'created':
      return 'Added manually';
    case 'stage_changed':
      const metadata = activity.metadata || {};
      let oldStage = metadata.oldStage;
      let newStage = metadata.newStage;
      if (!oldStage || !newStage) {
        const desc = activity.description || '';
        const match = desc.match(/Stage changed from (.+) to (.+)/i);
        if (match) {
          oldStage = oldStage || match[1];
          newStage = newStage || match[2];
        }
      }
      oldStage = oldStage || 'unknown';
      newStage = newStage || 'unknown';
      return `Stage changed from ${getStageDisplayName(oldStage)} to ${getStageDisplayName(newStage)}`;
    case 'assigned': {
      const metadata = activity.metadata || {};
      if (activity.description) return activity.description;
      if (metadata.assignedToName && metadata.oldAssignedToName) {
        return `Reassigned from ${metadata.oldAssignedToName} to ${metadata.assignedToName}`;
      }
      if (metadata.assignedToName) {
        return `Assigned to ${metadata.assignedToName}`;
      }
      return 'Assigned to a team member';
    }
    case 'wedding_linked':
      return 'Wedding linked';
    case 'note_added':
      return 'Note added';
    case 'file_uploaded':
      return 'File uploaded';
    case 'reminder_set':
      return 'Follow-up reminder set';
    case 'follow_up_completed':
      return 'Follow-up completed';
    case 'manual_activity':
      return activity.description || 'Manual activity logged';
    case 'questionnaire_sent':
    case 'questionnaire_completed':
      return activity.description || 'Questionnaire activity';
    default:
      return activity.description || 'Activity logged';
  }
};

const formatDateTime = (value) => {
  if (!value) return 'Not set';
  return new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatMemberLabel = (member) => {
  if (member.user?.firstName || member.user?.lastName) {
    return `${member.user.firstName || ''} ${member.user.lastName || ''}`.trim();
  }
  if (member.invitedEmail) return member.invitedEmail;
  return 'Team member';
};

function formatMailchimpMemberStatus(status) {
  const s = String(status || '').trim().toLowerCase();
  if (!s) return '';
  if (s === 'subscribed') return 'Subscribed';
  if (s === 'unsubscribed') return 'Unsubscribed';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const assigneeDisplayName = (assigned) => {
  if (!assigned) return '';
  if (assigned.user?.firstName || assigned.user?.lastName) {
    return `${assigned.user.firstName || ''} ${assigned.user.lastName || ''}`.trim();
  }
  if (assigned.name) return assigned.name;
  return 'Team member';
};

const LeadDetailView = () => {
  const styles = useThemeStyles();
  const navigate = useNavigate();
  const { leadId } = useParams();
  const { selectedBusiness, loading: businessLoading } = useBusiness();
  const businessId = selectedBusiness?.business?._id;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLinkWeddingModal, setShowLinkWeddingModal] = useState(false);
  const [weddingCode, setWeddingCode] = useState('');
  const [editData, setEditData] = useState({
    name: '',
    email: '',
    phone: '',
    weddingDate: '',
    leadSource: '',
    leadSourceOther: '',
    budgetRange: {
      min: '',
      max: ''
    },
    priority: '',
    stage: '',
    tags: [],
    weddingCode: '',
    unlinkWedding: false,
    assignedTo: ''
  });
  const [newEditTag, setNewEditTag] = useState('');
  const [teamMembers, setTeamMembers] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [stageOptions, setStageOptions] = useState([]);
  const [leadSourceOptions, setLeadSourceOptions] = useState(() => mergeLeadSources([]));
  const [saving, setSaving] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [dateWarnings, setDateWarnings] = useState(null);
  const [activityPage, setActivityPage] = useState(1);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [viewFileUrl, setViewFileUrl] = useState(null);
  const [viewFileName, setViewFileName] = useState('');
  const [viewAttachment, setViewAttachment] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [storageStats, setStorageStats] = useState(null);
  const [_storageLoading, setStorageLoading] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [declineBookingId, setDeclineBookingId] = useState(null);
  const [declineBusy, setDeclineBusy] = useState(false);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [reminderForm, setReminderForm] = useState({
    reminderDate: '',
    reminderTime: '',
    message: ''
  });
  const [showCompleteReminderModal, setShowCompleteReminderModal] = useState(false);
  const [reminderToComplete, setReminderToComplete] = useState(null);
  const [showDeleteReminderModal, setShowDeleteReminderModal] = useState(false);
  const [reminderToDelete, setReminderToDelete] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [proposalsInvoicesLoading, setProposalsInvoicesLoading] = useState(false);
  const [leadFormSubmissions, setLeadFormSubmissions] = useState([]);
  const [leadFormSubmissionsLoading, setLeadFormSubmissionsLoading] = useState(false);
  const [questionnaireSends, setQuestionnaireSends] = useState([]);
  const [questionnaireSendsLoading, setQuestionnaireSendsLoading] = useState(false);
  const [submissionDialogEntry, setSubmissionDialogEntry] = useState(null);
  const [submissionDialogForm, setSubmissionDialogForm] = useState(null);
  const [mailchimpStatus, setMailchimpStatus] = useState({ connected: false, hasAudience: false });
  const [mailchimpMember, setMailchimpMember] = useState({
    loading: false,
    error: false,
    checkable: false,
    inAudience: false,
    memberStatus: null,
    audienceName: null
  });
  const [mailchimpActionLoading, setMailchimpActionLoading] = useState(false);
  const [mailchimpActionMessage, setMailchimpActionMessage] = useState('');
  const [mailchimpActionError, setMailchimpActionError] = useState('');

  useEffect(() => {
    if (!leadId || !businessId || businessLoading) return;
    fetchLead();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [leadId, businessId, businessLoading]);

  useEffect(() => {
    if (!leadId || !businessId || businessLoading) return;
    let cancelled = false;
    const loadFormSubmissions = async () => {
      setLeadFormSubmissionsLoading(true);
      try {
        const { data } = await api.get(`/api/leads/${leadId}/form-submissions`, { params: { businessId } });
        if (!cancelled) {
          setLeadFormSubmissions(Array.isArray(data.submissions) ? data.submissions : []);
        }
      } catch {
        if (!cancelled) setLeadFormSubmissions([]);
      } finally {
        if (!cancelled) setLeadFormSubmissionsLoading(false);
      }
    };
    loadFormSubmissions();
    return () => {
      cancelled = true;
    };
  }, [leadId, businessId, businessLoading]);

  useEffect(() => {
    if (!leadId || !businessId || businessLoading) return;
    let cancelled = false;
    const loadQuestionnaireSends = async () => {
      setQuestionnaireSendsLoading(true);
      try {
        const { data } = await api.get(`/api/leads/${leadId}/questionnaire-sends`, { params: { businessId } });
        if (!cancelled) {
          setQuestionnaireSends(Array.isArray(data.sends) ? data.sends : []);
        }
      } catch {
        if (!cancelled) setQuestionnaireSends([]);
      } finally {
        if (!cancelled) setQuestionnaireSendsLoading(false);
      }
    };
    loadQuestionnaireSends();
    return () => {
      cancelled = true;
    };
  }, [leadId, businessId, businessLoading]);

  useEffect(() => {
    if (lead && lead.wedding) {
      fetchDateWarnings();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [lead]);

  useEffect(() => {
    if (leadId && businessId && !businessLoading) {
      fetchAttachments();
      fetchStorageStats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [leadId, businessId, businessLoading]);

  useEffect(() => {
    if (leadId && businessId && !businessLoading) {
      fetchReminders();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [leadId, businessId, businessLoading]);

  useEffect(() => {
    if (!businessId || businessLoading) return;
    const loadLeadSources = async () => {
      try {
        const response = await api.get('/api/crm/sources', { params: { businessId } });
        setLeadSourceOptions(mergeLeadSources(response.data?.sources || []));
      } catch (error) {
        setLeadSourceOptions(mergeLeadSources([]));
      }
    };
    loadLeadSources();
  }, [businessId, businessLoading]);

  useEffect(() => {
    if (!businessId || businessLoading) return;
    let cancelled = false;
    api.get('/api/mailchimp/status', { params: { businessId } })
      .then(({ data }) => {
        if (cancelled) return;
        setMailchimpStatus({
          connected: Boolean(data?.connected),
          hasAudience: Boolean(data?.hasAudience)
        });
      })
      .catch(() => {
        if (cancelled) return;
        setMailchimpStatus({ connected: false, hasAudience: false });
      });
    return () => {
      cancelled = true;
    };
  }, [businessId, businessLoading]);

  const loadMailchimpMemberStatus = useCallback(async () => {
    if (!businessId || !leadId || !lead) return;
    if (!mailchimpStatus.connected || !mailchimpStatus.hasAudience) {
      setMailchimpMember({
        loading: false,
        error: false,
        checkable: false,
        inAudience: false,
        memberStatus: null,
        audienceName: null
      });
      return;
    }
    const email = String(lead.email || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMailchimpMember({
        loading: false,
        error: false,
        checkable: false,
        inAudience: false,
        memberStatus: null,
        audienceName: null
      });
      return;
    }
    setMailchimpMember((prev) => ({ ...prev, loading: true, error: false }));
    try {
      const { data } = await api.get('/api/mailchimp/member-status', {
        params: { businessId, leadId }
      });
      setMailchimpMember({
        loading: false,
        error: false,
        checkable: Boolean(data?.checkable),
        inAudience: Boolean(data?.inAudience),
        memberStatus: data?.memberStatus ?? null,
        audienceName: data?.audienceName ?? null
      });
    } catch {
      setMailchimpMember({
        loading: false,
        error: true,
        checkable: false,
        inAudience: false,
        memberStatus: null,
        audienceName: null
      });
    }
  }, [businessId, leadId, lead, mailchimpStatus.connected, mailchimpStatus.hasAudience]);

  useEffect(() => {
    if (!leadId || !businessId || businessLoading || loading || !lead) return;
    loadMailchimpMemberStatus();
  }, [
    leadId,
    businessId,
    businessLoading,
    loading,
    lead,
    loadMailchimpMemberStatus
  ]);

  useEffect(() => {
    if (!businessId || businessLoading) return;
    const loadAvailableTags = async () => {
      try {
        const response = await api.get('/api/leads', {
          params: { businessId, page: 1, limit: 200 }
        });
        const tags = new Set();
        (response.data?.leads || []).forEach((item) => {
          if (!Array.isArray(item?.tags)) return;
          item.tags.forEach((tag) => {
            const cleanTag = normaliseTagValue(tag);
            if (cleanTag) tags.add(cleanTag);
          });
        });
        setAvailableTags(Array.from(tags).sort((a, b) => a.localeCompare(b)));
      } catch (error) {
        setAvailableTags([]);
      }
    };
    loadAvailableTags();
  }, [businessId, businessLoading]);

  useEffect(() => {
    if (lead?.business && leadId) {
      const businessId = lead.business?._id || lead.business;
      const fetchProposalsInvoices = async () => {
        setProposalsInvoicesLoading(true);
        try {
          const [proposalsRes, invoicesRes] = await Promise.all([
            api.get(`/api/agreements/business/${businessId}`, { params: { leadId } }),
            api.get(`/api/invoices/business/${businessId}`, { params: { leadId, limit: 50 } })
          ]);
          setProposals(proposalsRes.data || []);
          setInvoices(invoicesRes.data?.invoices || []);
        } catch {
          setProposals([]);
          setInvoices([]);
        } finally {
          setProposalsInvoicesLoading(false);
        }
      };
      fetchProposalsInvoices();
    }
  }, [lead?.business, leadId]);

  const fetchStorageStats = async () => {
    if (!businessId) return;
    try {
      setStorageLoading(true);
      const response = await api.get('/api/leads/attachments/storage-stats', { params: { businessId } });
      if (response.data) {
        setStorageStats(response.data);
      }
    } catch (error) {
      setStorageStats(null);
    } finally {
      setStorageLoading(false);
    }
  };

  const fetchDateWarnings = async () => {
    if (!businessId) return;
    try {
      const response = await api.get(`/api/leads/${leadId}/date-warnings`, { params: { businessId } });
      setDateWarnings(response.data);
    } catch (error) {}
  };

  const fetchLead = async () => {
    if (!businessId) return;
    try {
      setLoading(true);
      const response = await api.get(`/api/leads/${leadId}`, { params: { businessId } });
      setLead({
        ...response.data,
        tags: Array.isArray(response.data?.tags)
          ? response.data.tags.map(normaliseTagValue).filter(Boolean)
          : []
      });
    } catch (error) {} finally {
      setLoading(false);
    }
  };

  const handleLeadDeclineBooking = async () => {
    if (!declineBookingId) return;
    setDeclineBusy(true);
    try {
      await api.put(`/api/unified-bookings/${declineBookingId}/status`, {
        status: 'rejected',
        notifyClient: true
      });
      setDeclineBookingId(null);
      await fetchLead();
    } catch (e) {
      alert(e.response?.data?.message || 'Could not decline this booking.');
    } finally {
      setDeclineBusy(false);
    }
  };

  const fetchAttachments = async () => {
    if (!businessId) return;
    try {
      setAttachmentsLoading(true);
      const response = await api.get(`/api/leads/${leadId}/attachments`, { params: { businessId } });
      setAttachments(response.data || []);
    } catch (error) {} finally {
      setAttachmentsLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadError(null);
      setSelectedFile(file);
      setShowUploadDialog(true);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploadingFile(true);
      const formData = new FormData();
      formData.append('file', selectedFile);

      await api.post(`/api/leads/${leadId}/attachments`, formData, {
        params: { businessId },
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSelectedFile(null);
      setShowUploadDialog(false);
      setUploadError(null);
      fetchAttachments();
      fetchStorageStats(); // Refresh storage stats
      fetchLead(); // Refresh lead to update activity log
    } catch (error) {
      setUploadError(error.response?.data?.message || 'Failed to upload file. Please ensure it is a valid PDF, Word document, Excel file, PowerPoint file, image, or text file.');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      await api.delete(`/api/leads/${leadId}/attachments/${attachmentId}`, { params: { businessId } });
      fetchAttachments();
      fetchStorageStats(); // Refresh storage stats
      fetchLead(); // Refresh lead to update activity log
    } catch (error) {
      alert('Failed to delete file');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const fetchReminders = async () => {
    if (!businessId) return;
    try {
      setRemindersLoading(true);
      const response = await api.get(`/api/leads/${leadId}/reminders`, { params: { businessId } });
      setReminders(response.data || []);
    } catch (error) {} finally {
      setRemindersLoading(false);
    }
  };

  const handleCreateReminder = async () => {
    if (!reminderForm.reminderDate || !reminderForm.reminderTime || !reminderForm.message) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const dateTime = new Date(`${reminderForm.reminderDate}T${reminderForm.reminderTime}`);
      
      if (editingReminder) {
        // Update existing reminder
        await api.put(`/api/leads/${leadId}/reminders/${editingReminder._id}`, {
          reminderDate: dateTime.toISOString(),
          message: reminderForm.message
        }, { params: { businessId } });
      } else {
        // Create new reminder
        await api.post(`/api/leads/${leadId}/reminders`, {
          reminderDate: dateTime.toISOString(),
          message: reminderForm.message,
          assignedTo: lead.assignedTo?._id || lead.assignedTo || undefined
        }, { params: { businessId } });
      }

      setReminderForm({ reminderDate: '', reminderTime: '', message: '' });
      setEditingReminder(null);
      setShowReminderDialog(false);
      fetchReminders();
      fetchLead(); // Refresh lead to update activity log
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save reminder');
    }
  };

  const handleEditReminder = (reminder) => {
    const reminderDate = new Date(reminder.reminderDate);
    const dateStr = reminderDate.toISOString().split('T')[0];
    const timeStr = reminderDate.toTimeString().slice(0, 5);
    
    setEditingReminder(reminder);
    setReminderForm({
      reminderDate: dateStr,
      reminderTime: timeStr,
      message: reminder.message
    });
    setShowReminderDialog(true);
  };

  const handleCompleteReminder = async (reminderId) => {
    try {
      await api.put(`/api/leads/${leadId}/reminders/${reminderId}/complete`, {}, { params: { businessId } });
      setShowCompleteReminderModal(false);
      setReminderToComplete(null);
      fetchReminders();
      fetchLead(); // Refresh lead to update activity log
    } catch (error) {
      alert('Failed to mark reminder as complete');
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    try {
      await api.delete(`/api/leads/${leadId}/reminders/${reminderId}`, { params: { businessId } });
      setShowDeleteReminderModal(false);
      setReminderToDelete(null);
      fetchReminders();
      fetchLead(); // Refresh lead to update activity log
    } catch (error) {
      alert('Failed to delete reminder');
    }
  };

  const getReminderStatus = (reminder) => {
    const now = new Date();
    const reminderDate = new Date(reminder.reminderDate);
    
    if (reminder.isCompleted) {
      return { label: 'Completed', color: 'success' };
    } else if (reminderDate < now) {
      return { label: 'Overdue', color: 'error' };
    } else {
      return { label: 'Upcoming', color: 'info' };
    }
  };

  const handleLinkWedding = async () => {
    try {
      await api.post(`/api/leads/${leadId}/link-wedding`, { weddingCode, businessId });
      setShowLinkWeddingModal(false);
      setWeddingCode('');
      fetchLead(); // Refresh lead data
    } catch (error) {}
  };

  const handleStartChat = async () => {
    if (!lead.wedding) {
      return;
    }

    try {
      // Check if a chat already exists for this wedding
      const existingChatsResponse = await api.get('/api/chat/conversations');
      const existingChat = existingChatsResponse.data.find(chat => 
        chat.wedding && chat.wedding._id === lead.wedding
      );

      if (existingChat) {
        // Navigate to existing chat
        navigate(`/supplier/messages/${existingChat._id}`);
      } else {
        // Navigate to messages page - user can create new chat from there
        // The chat creation is typically initiated by couples, not suppliers
        navigate('/supplier/messages');
      }
    } catch (error) {
      // Fallback: navigate to messages page
      navigate('/supplier/messages');
    }
  };

  const handleAddCoupleToMailchimp = async () => {
    if (!lead?._id || !businessId) return;
    const wasInAudience =
      mailchimpMember.checkable && mailchimpMember.inAudience && !mailchimpMember.loading;
    setMailchimpActionLoading(true);
    setMailchimpActionError('');
    setMailchimpActionMessage('');
    try {
      await api.post('/api/mailchimp/add-member', {
        leadId: lead._id,
        businessId
      });
      await loadMailchimpMemberStatus();
      setMailchimpActionMessage(
        wasInAudience
          ? 'Updated this couple in your Mailchimp audience.'
          : 'Added this couple to your Mailchimp audience.'
      );
    } catch (error) {
      setMailchimpActionError(error?.response?.data?.message || 'Failed to add couple to Mailchimp.');
    } finally {
      setMailchimpActionLoading(false);
    }
  };

  const openEditModal = async () => {
    if (lead) {
      // Map old priority values to new values for display
      const priorityMapToNew = {
        'hot': 'high',
        'warm': 'medium',
        'cold': 'low'
      };
      const displayPriority = lead.priority && priorityMapToNew[lead.priority] 
        ? priorityMapToNew[lead.priority] 
        : (lead.priority || '');
      
      setEditData({
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        weddingDate: lead.weddingDate ? new Date(lead.weddingDate).toISOString().split('T')[0] : '',
        leadSource: lead.leadSource || 'manual',
        leadSourceOther: lead.leadSourceOther || '',
        budgetRange: {
          min: lead?.budgetRange?.min ?? '',
          max: lead?.budgetRange?.max ?? ''
        },
        priority: displayPriority,
        stage: lead.stageSlug || lead.stage || '',
        tags: Array.isArray(lead.tags)
          ? lead.tags.map(normaliseTagValue).filter(Boolean)
          : [],
        weddingCode: lead.weddingCode || '',
        unlinkWedding: false,
        assignedTo: lead.assignedTo?._id ? String(lead.assignedTo._id) : lead.assignedTo ? String(lead.assignedTo) : ''
      });
    }
    setNewEditTag('');
    setShowEditModal(true);
    try {
      const response = await api.get('/api/crm/stages');
      setStageOptions(response.data?.stages || []);
    } catch (err) {}
    if (businessId) {
      try {
        const membersRes = await api.get(`/api/business-members/business/${businessId}/for-lead-assignment`);
        setTeamMembers(membersRes.data?.members || []);
      } catch (err) {
        setTeamMembers([]);
      }
    }
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddEditTag = (tagValue = newEditTag) => {
    const resolvedTagValue = tagValue?.nativeEvent ? newEditTag : tagValue;
    const cleanTag = normaliseTagValue(resolvedTagValue);
    if (!cleanTag) return;
    if (editData.tags.includes(cleanTag)) return;
    setEditData(prev => ({ ...prev, tags: [...prev.tags, cleanTag] }));
    if (resolvedTagValue === newEditTag) {
      setNewEditTag('');
    }
  };

  const handleRemoveEditTag = (tagToRemove) => {
    setEditData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSaveLead = async () => {
    if (editData.leadSource === 'other' && !String(editData.leadSourceOther || '').trim()) {
      alert('Please tell us where this enquiry came from');
      return;
    }

    try {
      setSaving(true);
      // Map new priority values to old values for backend compatibility
      const priorityMapToOld = {
        'high': 'hot',
        'medium': 'warm',
        'low': 'cold'
      };
      const backendPriority = editData.priority && priorityMapToOld[editData.priority]
        ? priorityMapToOld[editData.priority]
        : editData.priority;
      
      const payload = {
        name: editData.name,
        email: editData.email,
        phone: editData.phone,
        weddingDate: editData.weddingDate || null,
        leadSource: editData.leadSource,
        leadSourceOther: editData.leadSource === 'other' ? editData.leadSourceOther : '',
        budgetRange: {
          min: editData.budgetRange.min === '' ? undefined : Number(editData.budgetRange.min),
          max: editData.budgetRange.max === '' ? undefined : Number(editData.budgetRange.max)
        },
        priority: backendPriority,
        stage: editData.stage,
        tags: (editData.tags || []).map(normaliseTagValue).filter(Boolean),
        weddingCode: editData.weddingCode,
        unlinkWedding: editData.unlinkWedding
      };
      const prevAssignId = lead?.assignedTo?._id ? String(lead.assignedTo._id) : lead?.assignedTo ? String(lead.assignedTo) : '';
      const newAssignId = editData.assignedTo ? String(editData.assignedTo) : '';

      await api.put(`/api/leads/${leadId}/details`, { ...payload, businessId });

      if (prevAssignId !== newAssignId) {
        await api.put(`/api/leads/${leadId}/assign`, { assignedTo: newAssignId || null, businessId });
      }

      await fetchLead();
      setShowEditModal(false);
    } catch (err) {} finally {
      setSaving(false);
    }
  };

  const handleArchiveLead = async () => {
    try {
      setSaving(true);
      await api.put(`/api/leads/${leadId}/archive`, { businessId });
      navigate('/supplier/crm');
    } catch (err) {} finally {
      setSaving(false);
    }
  };

  const handleUnarchiveLead = async () => {
    try {
      setSaving(true);
      await api.put(`/api/leads/${leadId}/unarchive`, { businessId });
      fetchLead();
    } catch (err) {} finally {
      setSaving(false);
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      enquired: 'default',
      in_progress: 'info',
      completed: 'success',
      archived: 'default'
    };
    return colors[stage] || 'default';
  };

  // Map old priority values to new values for display
  const mapPriorityForDisplay = (priority) => {
    const priorityMap = {
      'hot': 'high',
      'warm': 'medium',
      'cold': 'low'
    };
    return priorityMap[priority] || priority;
  };
  
  const getPriorityColor = (priority) => {
    // Map to new value first for consistent color mapping
    const mappedPriority = mapPriorityForDisplay(priority);
    const colors = {
      'high': 'error',
      'medium': 'warning',
      'low': 'default',
      // Legacy support for old values (fallback)
      'hot': 'error',
      'warm': 'warning',
      'cold': 'default'
    };
    return colors[mappedPriority] || colors[priority] || 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading…</Typography>
      </Box>
    );
  }

  if (!lead) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>We could not find this record</Typography>
      </Box>
    );
  }

  const hasValidLeadEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(lead.email || '').trim());
  const canAddToMailchimp = hasValidLeadEmail && mailchimpStatus.connected && mailchimpStatus.hasAudience;
  const mailchimpInAudienceChip =
    mailchimpMember.checkable &&
    mailchimpMember.inAudience &&
    mailchimpMember.memberStatus &&
    formatMailchimpMemberStatus(mailchimpMember.memberStatus);
  const mailchimpAudienceLabelSafe = mailchimpMember.audienceName || 'your selected audience';

  const customFieldEntries = (() => {
    if (!lead?.customFields) return [];
    const raw = lead.customFields;
    const entries = raw instanceof Map ? Array.from(raw.entries()) : Object.entries(raw);
    return entries.filter(([, value]) => value != null && String(value).trim() !== '');
  })();

  const formatCustomFieldLabel = (key) =>
    String(key || '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());

  let mailchimpActionTooltip =
    !hasValidLeadEmail
      ? 'This couple needs a valid email address first'
      : !mailchimpStatus.connected
        ? 'Connect Mailchimp in Apps first'
        : !mailchimpStatus.hasAudience
          ? 'Choose a Mailchimp audience in Apps first'
          : '';

  if (canAddToMailchimp && !mailchimpActionTooltip) {
    if (mailchimpMember.loading) {
      mailchimpActionTooltip = 'Checking whether this email is already in Mailchimp…';
    } else if (mailchimpMember.error) {
      mailchimpActionTooltip =
        'We could not verify Mailchimp for this couple. You can still try to sync.';
    } else if (mailchimpMember.checkable && mailchimpMember.inAudience) {
      mailchimpActionTooltip = `Already in ${mailchimpAudienceLabelSafe}. Click to refresh name and phone in Mailchimp from this record.`;
    } else if (mailchimpMember.checkable) {
      mailchimpActionTooltip = `Not in ${mailchimpAudienceLabelSafe} yet. Click to add this couple.`;
    } else {
      mailchimpActionTooltip = 'Add this couple to your Mailchimp audience.';
    }
  }

  const mailchimpActionButtonLabel =
    mailchimpActionLoading
      ? 'Syncing…'
      : mailchimpMember.checkable && mailchimpMember.inAudience && !mailchimpMember.loading
        ? 'Update in Mailchimp'
        : 'Add couple to Mailchimp';

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      {/* Header */}
      <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} alignItems={isMobile ? 'stretch' : 'center'} gap={2} mb={3}>
        <Box display="flex" alignItems="center" flex={1}>
          <IconButton onClick={() => navigate('/supplier/crm')} sx={{ mr: 2 }}>
            <ArrowLeft size={24} />
          </IconButton>
          <Box flexGrow={1} minWidth={0}>
            <Typography component="h2" className="pagename" sx={styles.text.pagename}>
              {lead.name}
            </Typography>
            <Box display="flex" alignItems="center" gap={2} mt={1} flexWrap="wrap">
              <Chip 
                label={(lead.stageName || lead.stage || 'Stage').toUpperCase()} 
                color={getStageColor(lead.stageSlug || lead.stage)}
                size="small"
              />
              <Chip 
                label={mapPriorityForDisplay(lead.priority)?.toUpperCase() || 'N/A'} 
                color={getPriorityColor(lead.priority)}
                size="small"
              />
              {lead.assignedTo && (
                <Chip 
                  label={`Assigned to ${assigneeDisplayName(lead.assignedTo)}`}
                  color="info"
                  size="small"
                />
              )}
              {mailchimpStatus.connected && mailchimpStatus.hasAudience && hasValidLeadEmail ? (
                <>
                  {mailchimpMember.loading ? (
                    <Chip size="small" variant="outlined" label="Checking Mailchimp…" />
                  ) : mailchimpMember.error ? (
                    <Chip size="small" color="warning" variant="outlined" label="Mailchimp status unknown" />
                  ) : mailchimpMember.checkable && mailchimpMember.inAudience ? (
                    <Chip
                      size="small"
                      color="success"
                      label={
                        mailchimpInAudienceChip
                          ? `In Mailchimp · ${mailchimpInAudienceChip}`
                          : 'In Mailchimp audience'
                      }
                    />
                  ) : mailchimpMember.checkable ? (
                    <Chip size="small" variant="outlined" label="Not in Mailchimp audience yet" />
                  ) : null}
                </>
              ) : null}
            </Box>
          </Box>
        </Box>
        {!isMobile && (
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<PencilSimple size={20} />}
              onClick={openEditModal}
              sx={{ bgcolor: '#000', color: '#fff', '&:hover': { bgcolor: '#333' } }}
            >
              Edit
            </Button>
            {mailchimpStatus.connected ? (
              <Tooltip title={mailchimpActionTooltip}>
                <span>
                  <Button
                    variant="outlined"
                    onClick={handleAddCoupleToMailchimp}
                    disabled={!canAddToMailchimp || mailchimpActionLoading}
                  >
                    {mailchimpActionButtonLabel}
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {(lead.stageSlug === 'archived' || lead.stage === 'archived' || lead.status === 'archived') ? (
            <Button
              variant="contained"
              startIcon={<Archive size={20} />}
              onClick={handleUnarchiveLead}
              disabled={saving}
              sx={{ bgcolor: '#ef4192', color: '#fff', '&:hover': { bgcolor: '#d63681' } }}
            >
              Unarchive
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<Archive size={20} />}
              onClick={handleArchiveLead}
              disabled={saving}
              sx={{ bgcolor: '#ef4192', color: '#fff', '&:hover': { bgcolor: '#d63681' } }}
            >
              Archive
            </Button>
          )}
          </Box>
        )}
      </Box>
      {/* Date Warning Banner */}
      {dateWarnings && dateWarnings.hasWarnings && dateWarnings.warnings && dateWarnings.warnings.length > 0 && (
        <Alert 
          severity="warning" 
          icon={<Warning size={20} />}
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Date Mismatch Detected
          </Typography>
          {dateWarnings.warnings.map((warning, idx) => {
            const eventDate = warning.eventDate ? new Date(warning.eventDate).toLocaleDateString('en-GB') : 'Unknown';
            const ceremonyDate = warning.alignment?.ceremonyDate ? new Date(warning.alignment.ceremonyDate).toLocaleDateString('en-GB') : null;
            const receptionDate = warning.alignment?.receptionDate ? new Date(warning.alignment.receptionDate).toLocaleDateString('en-GB') : null;
            
            return (
              <Box key={idx} sx={{ mt: idx > 0 ? 1 : 0 }}>
                <Typography variant="body2" component="div">
                  <strong>{warning.eventTitle || 'Event'}</strong> scheduled for <strong>{eventDate}</strong> does not match the wedding date set in the couple's planner.
                  {ceremonyDate || receptionDate ? (
                    <>
                      {' '}The couple has set their wedding date as{' '}
                      {ceremonyDate && receptionDate ? (
                        <>Ceremony: {ceremonyDate} or Reception: {receptionDate}.</>
                      ) : ceremonyDate ? (
                        <>{ceremonyDate}.</>
                      ) : (
                        <>{receptionDate}.</>
                      )}
                      {' '}Please reach out to discuss this with them and update the date in their planner if they have the coordinator plan.
                    </>
                  ) : (
                    <> Wedding dates not set in the couple's planner. Please reach out to discuss this with them and update the date if they have the coordinator plan.</>
                  )}
                </Typography>
              </Box>
            );
          })}
        </Alert>
      )}
      {mailchimpActionError ? (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setMailchimpActionError('')}>
          {mailchimpActionError}
        </Alert>
      ) : null}
      {mailchimpActionMessage ? (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setMailchimpActionMessage('')}>
          {mailchimpActionMessage}
        </Alert>
      ) : null}
      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left column: contact and wedding link */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
              {lead.email && (
                <Box mb={2}>
                  <Box display="flex" alignItems="center">
                    <Envelope size={20} style={{ marginRight: 8 }} />
                    <Typography>{lead.email}</Typography>
                  </Box>
                  {mailchimpStatus.connected && mailchimpStatus.hasAudience && hasValidLeadEmail ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 0.75, pl: '28px', maxWidth: 320 }}
                    >
                      {mailchimpMember.loading
                        ? 'Checking Mailchimp for this address…'
                        : mailchimpMember.error
                          ? 'Mailchimp: we could not check this address. You can still use the button below to sync.'
                          : mailchimpMember.checkable && mailchimpMember.inAudience
                            ? mailchimpInAudienceChip
                              ? `Mailchimp: already in ${mailchimpAudienceLabelSafe} (${mailchimpInAudienceChip}).`
                              : `Mailchimp: already in ${mailchimpAudienceLabelSafe}.`
                            : mailchimpMember.checkable
                              ? `Mailchimp: not in ${mailchimpAudienceLabelSafe} yet.`
                              : ''}
                    </Typography>
                  ) : null}
                </Box>
              )}
              {lead.phone && (
                <Box display="flex" alignItems="center" mb={2}>
                  <Phone size={20} style={{ marginRight: 8 }} />
                  <Typography>{lead.phone}</Typography>
                </Box>
              )}
              {lead.weddingDate && (
                <Box display="flex" alignItems="center" mb={2}>
                  <Calendar size={20} style={{ marginRight: 8 }} />
                  <Typography>
                    Wedding Date: {new Date(lead.weddingDate).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
              {lead.budgetRange && (
                <Box display="flex" alignItems="center" mb={2}>
                  <Tag size={20} style={{ marginRight: 8 }} />
                  <Typography>
                    Budget: £{lead.budgetRange.min || 0} - £{lead.budgetRange.max || 'No limit'}
                  </Typography>
                </Box>
              )}
              {lead.tags && lead.tags.length > 0 && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Tags
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {lead.tags.map((tag, index) => (
                      <Chip key={index} label={tag} size="small" />
                    ))}
                  </Box>
                </Box>
              )}
              {customFieldEntries.length > 0 && (
                <Box mt={3} pt={3} sx={{ borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" gutterBottom>
                    Additional information
                  </Typography>
                  {customFieldEntries.map(([key, value]) => (
                    <Box key={key} mb={1.5}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {formatCustomFieldLabel(key)}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              {lead.wedding?.details?.partners && (
                <Box mt={3} pt={3} sx={{ borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" gutterBottom>
                    Couple Information
                  </Typography>
                  {lead.wedding.details.partners.partnerOne && (
                    <Box mb={3}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <Avatar
                          src={lead.wedding.details.partners.partnerOne.profilePicture}
                          sx={{ width: 40, height: 40, mr: 1 }}
                        >
                          {lead.wedding.details.partners.partnerOne.fullName
                            ? lead.wedding.details.partners.partnerOne.fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                            : 'P1'
                          }
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {lead.wedding.details.partners.partnerOne.fullName || 'Partner One'}
                          </Typography>
                          {lead.wedding.details.partners.partnerOne.preferredName && 
                           lead.wedding.details.partners.partnerOne.preferredName !== lead.wedding.details.partners.partnerOne.fullName && (
                            <Typography variant="body2" color="text.secondary">
                              Preferred: {lead.wedding.details.partners.partnerOne.preferredName}
                            </Typography>
                          )}
                          {lead.wedding.details.partners.partnerOne.pronouns && (
                            <Typography variant="body2" color="text.secondary">
                              {lead.wedding.details.partners.partnerOne.pronouns}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}
                  {lead.wedding.details.partners.partnerTwo && (
                    <Box mb={2}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <Avatar
                          src={lead.wedding.details.partners.partnerTwo.profilePicture}
                          sx={{ width: 40, height: 40, mr: 1 }}
                        >
                          {lead.wedding.details.partners.partnerTwo.fullName
                            ? lead.wedding.details.partners.partnerTwo.fullName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                            : 'P2'
                          }
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {lead.wedding.details.partners.partnerTwo.fullName || 'Partner Two'}
                          </Typography>
                          {lead.wedding.details.partners.partnerTwo.preferredName && 
                           lead.wedding.details.partners.partnerTwo.preferredName !== lead.wedding.details.partners.partnerTwo.fullName && (
                            <Typography variant="body2" color="text.secondary">
                              Preferred: {lead.wedding.details.partners.partnerTwo.preferredName}
                            </Typography>
                          )}
                          {lead.wedding.details.partners.partnerTwo.pronouns && (
                            <Typography variant="body2" color="text.secondary">
                              {lead.wedding.details.partners.partnerTwo.pronouns}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
              {isMobile && (
                <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<PencilSimple size={20} />}
                    onClick={openEditModal}
                    sx={{ bgcolor: '#000', color: '#fff', '&:hover': { bgcolor: '#333' } }}
                  >
                    Edit
                  </Button>
                  {mailchimpStatus.connected ? (
                    <Tooltip title={mailchimpActionTooltip}>
                      <span style={{ width: '100%', display: 'block' }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          sx={{ mt: 1.5 }}
                          onClick={handleAddCoupleToMailchimp}
                          disabled={!canAddToMailchimp || mailchimpActionLoading}
                        >
                          {mailchimpActionButtonLabel}
                        </Button>
                      </span>
                    </Tooltip>
                  ) : null}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Wedding Information */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Wedding Information
                </Typography>
                {!lead.wedding && (
                  <Button
                    size="small"
                    onClick={() => setShowLinkWeddingModal(true)}
                  >
                    Link Wedding
                  </Button>
                )}
              </Box>
              {lead.wedding ? (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Wedding Code: {lead.weddingCode}
                  </Typography>
                  {lead.weddingDate && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Wedding Date: {new Date(lead.weddingDate).toLocaleDateString('en-GB')}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Linked: {new Date(lead.createdAt).toLocaleDateString()}
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        const weddingId = lead.wedding?._id || lead.wedding;
                        if (weddingId) {
                          navigate(`/supplier/wedding/${weddingId}/details`);
                        }
                      }}
                    >
                      View Wedding Details
                    </Button>
                    <Tooltip title={!lead.wedding ? "Link a wedding first to start a chat" : "Open chat with this couple"}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleStartChat}
                        disabled={!lead.wedding}
                      >
                        Start Chat
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No wedding linked yet
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Independent Cards */}
        <Grid item xs={12} md={8}>
          {/* Overview Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Overview
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Created
                        </Typography>
                        <Typography>
                          {new Date(lead.createdAt).toLocaleDateString()}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Last Contacted
                        </Typography>
                        <Typography>
                          {lead.lastChatMessageAt 
                            ? formatDateTime(lead.lastChatMessageAt)
                            : lead.lastContactedAt 
                            ? formatDateTime(lead.lastContactedAt)
                            : 'Never'
                          }
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Next Follow-up
                        </Typography>
                        <Typography>
                          {lead.nextFollowUpDate 
                            ? new Date(lead.nextFollowUpDate).toLocaleDateString()
                            : 'Not scheduled'
                          }
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Source
                        </Typography>
                        <Typography>
                          {lead.leadSource === 'other' && lead.leadSourceOther
                            ? `${getLeadSourceLabel(lead.leadSource, leadSourceOptions)} (${lead.leadSourceOther})`
                            : getLeadSourceLabel(lead.leadSource, leadSourceOptions)}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Stage
                        </Typography>
                        <Typography>
                          {getStageDisplayName(lead.stageName || lead.stageSlug || lead.stage)}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Priority
                        </Typography>
                        <Typography>
                          {capitalizeFirst(mapPriorityForDisplay(lead.priority)) || 'Not set'}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
            </CardContent>
          </Card>

          {/* Activity Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Activity Timeline
                  </Typography>
                  {lead.activityLog && lead.activityLog.length > 0 ? (
                    <Box sx={{ maxHeight: 400, overflowY: 'auto', pr: 0.5 }}>
                    <Box sx={{ position: 'relative', pl: 3 }}>
                      {[...lead.activityLog].reverse().slice(0, activityPage * 10).map((activity, index, array) => (
                        <Box key={index} sx={{ position: 'relative', pb: 3 }}>
                          {/* Timeline connector line */}
                          {index < array.length - 1 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                left: '-13px',
                                top: '24px',
                                width: '2px',
                                height: 'calc(100% - 8px)',
                                bgcolor: 'divider'
                              }}
                            />
                          )}
                          {/* Timeline dot */}
                          <Box
                            sx={{
                              position: 'absolute',
                              left: '-17px',
                              top: '4px',
                              width: '12px',
                              height: '12px',
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                              border: '2px solid',
                              borderColor: 'background.paper',
                              zIndex: 1
                            }}
                          />
                          {/* Activity content */}
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                              {getActivityDescription(activity)}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                              <Typography variant="body2" color="text.secondary">
                                {formatDateTime(activity.timestamp)}
                              </Typography>
                              {activity.createdBy && activity.type !== 'questionnaire_completed' && (
                                <>
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      bgcolor: 'primary.main',
                                      display: 'inline-block'
                                    }}
                                  />
                                  <Typography variant="body2" color="text.secondary">
                                    {activity.createdBy.firstName && activity.createdBy.lastName
                                      ? `${activity.createdBy.firstName} ${activity.createdBy.lastName}`
                                      : activity.createdBy.firstName || activity.createdBy.email || 'Unknown'
                                    }
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                      {lead.activityLog.length > activityPage * 10 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                          <Button 
                            variant="outlined" 
                            onClick={() => setActivityPage(prev => prev + 1)}
                          >
                            Load More
                          </Button>
                        </Box>
                      )}
                    </Box>
                    </Box>
                  ) : (
                    <Typography color="text.secondary">
                      No activity recorded yet
                    </Typography>
                  )}
            </CardContent>
          </Card>

          {/* Form submissions and questionnaires (responses linked to this couple) */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Article size={22} />
                Form submissions & questionnaires
              </Typography>
              {leadFormSubmissionsLoading && questionnaireSendsLoading ? (
                <Typography color="text.secondary">Loading…</Typography>
              ) : (
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Form submissions ({leadFormSubmissions.length})
                    </Typography>
                    {leadFormSubmissions.length === 0 ? (
                      <Typography color="text.secondary">
                        No form responses are linked to this couple yet.
                      </Typography>
                    ) : (
                      <Stack spacing={1.5}>
                        {leadFormSubmissions.map((sub) => {
                          const formDoc = sub.form;
                          const kindLabel =
                            formDoc?.kind === 'questionnaire' ? 'Questionnaire' : 'Website form';
                          return (
                            <Box
                              key={sub._id}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                gap: 2,
                                flexWrap: 'wrap'
                              }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600}>
                                  {formDoc?.name || 'Form'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {kindLabel}
                                  {' · '}
                                  Submitted {formatDateTime(sub.createdAt)}
                                </Typography>
                              </Box>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Eye size={18} />}
                                onClick={() => {
                                  setSubmissionDialogEntry({
                                    ...sub,
                                    formName: formDoc?.name
                                  });
                                  setSubmissionDialogForm(formDoc || null);
                                }}
                              >
                                View responses
                              </Button>
                            </Box>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Questionnaires sent ({questionnaireSends.length})
                    </Typography>
                    {questionnaireSends.length === 0 ? (
                      <Typography color="text.secondary">
                        No questionnaire sends linked to this couple yet.
                      </Typography>
                    ) : (
                      <Stack spacing={1.25}>
                        {questionnaireSends.map((send) => (
                          <Box
                            key={send._id}
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              gap: 2,
                              flexWrap: 'wrap'
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {send.form?.name || 'Questionnaire'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Sent {formatDateTime(send.sentAt || send.createdAt)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                size="small"
                                color={send.status === 'completed' ? 'success' : 'default'}
                                label={send.status === 'completed' ? 'Completed' : 'Pending'}
                                variant="outlined"
                              />
                              {send.publicUrl && (
                                <Button
                                  size="small"
                                  variant="text"
                                  onClick={() => window.open(send.publicUrl, '_blank', 'noopener,noreferrer')}
                                >
                                  Open link
                                </Button>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* Proposals & Invoices Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Agreements & invoices
              </Typography>
              {proposalsInvoicesLoading ? (
                <Typography color="text.secondary">Loading…</Typography>
              ) : proposals.length === 0 && invoices.length === 0 ? (
                <Typography color="text.secondary">No agreements or invoices for this couple yet</Typography>
              ) : (
                <List>
                  {proposals.map((p) => (
                    <ListItem
                      key={p._id}
                      button
                      onClick={() => navigate(`/supplier/agreements/${p._id}`)}
                      sx={{ px: 0 }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <FileText size={20} />
                      </ListItemIcon>
                      <ListItemText
                        primary="Agreement"
                        secondary={formatDateTime(p.createdAt)}
                      />
                      <ListItemSecondaryAction>
                        <Chip label={p.status} size="small" variant="outlined" />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                  {invoices.map((inv) => (
                    <ListItem
                      key={inv._id}
                      button
                      onClick={() => navigate(`/supplier/invoices/${inv._id}`)}
                      sx={{ px: 0 }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Receipt size={20} />
                      </ListItemIcon>
                      <ListItemText
                        primary={`${inv.invoiceNumber || 'Invoice'} ${inv.description ? `– ${inv.description}` : ''}`}
                        secondary={formatDateTime(inv.createdAt)}
                      />
                      <ListItemSecondaryAction>
                        <StatusBadge status={inv.status} />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {/* Events & Appointments Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box>
                  <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems={isMobile ? 'stretch' : 'center'} gap={2} mb={2}>
                    <Typography variant="h6">
                      Events & Appointments
                    </Typography>
                    <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                      <Chip
                        label={`${(lead.bookings?.length || 0) + (lead.events?.length || 0)} total`}
                        size="small"
                        variant="outlined"
                      />
                      <Button
                        variant="contained"
                        startIcon={<Plus size={20} />}
                        onClick={() => {
                          setEditingEvent(null);
                          setShowEventForm(true);
                        }}
                      >
                        Create Event
                      </Button>
                    </Box>
                  </Box>
                  
                  {/* Events Section */}
                  {lead.events && lead.events.length > 0 && (
                    <Box mb={3}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Events ({lead.events.length})
                      </Typography>
                      <List>
                        {lead.events.map((eventLink, index) => {
                          // Only use events that have been populated with eventData
                          // If eventData doesn't exist, the backend failed to populate it
                          if (!eventLink.eventData) {
                            return null;
                          }
                          
                          const event = eventLink.eventData;
                          if (!event || typeof event !== 'object') {
                            return null;
                          }

                          // Handle both populated and unpopulated event data
                          // event.title should always exist, but fallback to eventType or type
                          const eventTitle = event.title || event.eventType || event.type || 'Event';
                          // eventType is the custom field, type is the enum field
                          const eventType = event.eventType || (event.type && event.type !== 'other' ? event.type : null);
                          const start = event.startTime ? formatDateTime(event.startTime) : 'Date and time to be confirmed';
                          const end = event.endTime ? formatDateTime(event.endTime) : null;
                          const isBlocker = event.isBlocker !== false;

                          return (
                            <ListItem 
                              key={`event-${index}`} 
                              sx={{ 
                                alignItems: 'flex-start', 
                                py: 2, 
                                bgcolor: 'action.hover', 
                                borderRadius: 1, 
                                mb: 1,
                                position: 'relative'
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="subtitle1" fontWeight={600} component="span">
                                      {eventTitle}
                                    </Typography>
                                    {eventType && eventType !== 'Event' && (
                                      <Chip 
                                        label={eventType} 
                                        size="small" 
                                        color={isBlocker ? 'error' : 'default'}
                                        variant="outlined"
                                      />
                                    )}
                                    {isBlocker && (
                                      <Chip label="Blocking" size="small" color="error" />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <Box component="div" display="flex" flexDirection="column" gap={0.5} mt={0.5}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Calendar size={16} />
                                      <Typography variant="body2" color="text.secondary" component="span">
                                        {start}{end ? ` → ${end}` : ''}
                                      </Typography>
                                    </Box>
                                    {event.location?.address && (
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <MapPin size={16} />
                                        <Typography variant="body2" color="text.secondary" component="span">
                                          {event.location.address}
                                        </Typography>
                                      </Box>
                                    )}
                                    {event.description && (
                                      <Typography variant="body2" color="text.secondary" component="span">
                                        {event.description}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                              <IconButton
                                size="small"
                                onClick={() => {
                                  // Ensure we have the event ID - use event._id from eventData or fallback to eventLink.event
                                  const eventToEdit = {
                                    ...event,
                                    _id: event._id || eventLink.event
                                  };
                                  setEditingEvent(eventToEdit);
                                  setShowEventForm(true);
                                }}
                                sx={{ ml: 1 }}
                                title="Edit event"
                              >
                                <PencilSimple size={18} />
                              </IconButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Box>
                  )}

                  {/* Bookings Section */}
                  {lead.bookings && lead.bookings.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Appointments ({lead.bookings.length})
                      </Typography>
                      <List>
                        {lead.bookings.map((bookingLink, index) => {
                          const booking = bookingLink.booking;
                          if (!booking) return null;

                          const serviceName = booking?.serviceType?.name || booking?.title || 'Appointment';
                          const status = booking?.status || 'pending';
                          const rawStart = booking?.startTime;
                          const start =
                            rawStart && !Number.isNaN(new Date(rawStart).getTime())
                              ? formatDateTime(rawStart)
                              : 'Date and time to be confirmed';
                          const end =
                            booking?.endTime && !Number.isNaN(new Date(booking.endTime).getTime())
                              ? formatDateTime(booking.endTime)
                              : null;

                          const isFormEmbedBooking =
                            booking?.isPublicEmbedBooking ||
                            booking?.metadata?.source === 'public_embed';

                          const sourceLabel = isFormEmbedBooking
                            ? 'Form Embed'
                            : booking?.isManualBooking
                              ? 'Manual appointment'
                              : bookingLink.type === 'unified_booking'
                                ? 'Couple request'
                                : 'Business booking';

                          const guestName = getBookingGuestDisplayName(booking);
                          const guestEmail = getBookingGuestEmailLine(booking);

                          return (
                            <ListItem
                              key={`booking-${index}`}
                              sx={{
                                alignItems: 'flex-start',
                                py: 2,
                                bgcolor: 'action.hover',
                                borderRadius: 1,
                                mb: 1,
                                position: 'relative',
                                flexDirection: 'column',
                                alignItems: 'stretch'
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                    <Typography variant="subtitle1" fontWeight={600} component="span">
                                      {serviceName}
                                    </Typography>
                                    {status && status !== 'confirmed' && (
                                      <Chip label={status} size="small" variant="outlined" />
                                    )}
                                  </Box>
                                }
                                secondary={
                                  <Box component="div" display="flex" flexDirection="column" gap={0.5} mt={0.5}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                      <Calendar size={16} />
                                      <Typography variant="body2" color="text.secondary" component="span">
                                        {start}
                                        {end ? ` → ${end}` : ''}
                                      </Typography>
                                    </Box>
                                    {guestName ? (
                                      <Typography variant="caption" color="text.secondary" component="span">
                                        {guestEmail ? `${guestName} • ${guestEmail}` : guestName}
                                      </Typography>
                                    ) : null}
                                    <Typography variant="caption" color="text.secondary" component="span">
                                      Source: {sourceLabel}
                                    </Typography>
                                    {status === 'pending' && booking._id ? (
                                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                                        <Button
                                          size="small"
                                          variant="contained"
                                          color="success"
                                          onClick={() =>
                                            navigate('/supplier/bookings', {
                                              state: { openBookingApprove: booking._id }
                                            })
                                          }
                                        >
                                          Accept booking
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          color="error"
                                          onClick={() => setDeclineBookingId(String(booking._id))}
                                        >
                                          Decline
                                        </Button>
                                      </Stack>
                                    ) : null}
                                  </Box>
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    </Box>
                  )}

                  {/* Empty State */}
                  {(!lead.bookings || lead.bookings.length === 0) && (!lead.events || lead.events.length === 0) && (
                    <Typography color="text.secondary">
                      No events or appointments linked yet
                    </Typography>
                  )}
              </Box>
            </CardContent>
          </Card>

          {/* Notes Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Notes
              </Typography>
              <NotesEditor 
                  leadId={leadId}
                  onNoteAdded={() => {
                    // Refresh lead data if needed
                    fetchLead();
                  }}
                  onNoteUpdated={() => {
                    // Refresh lead data if needed
                    fetchLead();
                  }}
                  onNoteDeleted={() => {
                    // Refresh lead data if needed
                    fetchLead();
                  }}
                />
            </CardContent>
          </Card>

          {/* Files Card */}
          <SupplierFeatureGate feature="lead_file_upload" reason="File uploads for each couple">
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box>
                    {uploadError && (
                      <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
                        {uploadError}
                      </Alert>
                    )}
                    <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems={isMobile ? 'stretch' : 'center'} gap={2} mb={2}>
                      <Typography variant="h6">
                        Files & Attachments
                      </Typography>
                      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                        <input
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp"
                          style={{ display: 'none' }}
                          id="file-upload-input"
                          type="file"
                          onChange={handleFileSelect}
                        />
                        <label htmlFor="file-upload-input">
                          <Button
                            component="span"
                            startIcon={<Paperclip size={20} />}
                            variant="contained"
                            size="small"
                            disabled={uploadingFile}
                          >
                            {uploadingFile ? 'Uploading...' : 'Upload File'}
                          </Button>
                        </label>
                      </Box>
                    </Box>
                  {attachmentsLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                      <Typography>Loading attachments...</Typography>
                    </Box>
                  ) : attachments.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                      <Paperclip size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No files uploaded yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        Upload PDFs, Word documents, Excel files, PowerPoint files, images, or text files.
                      </Typography>
                    </Paper>
                  ) : (
                    <>
                      <List>
                        {attachments.map((attachment) => (
                          <ListItem
                            key={attachment._id}
                            sx={{
                              border: 1,
                              borderColor: 'divider',
                              borderRadius: 1,
                              mb: 1,
                              '&:hover': {
                                bgcolor: 'action.hover'
                              }
                            }}
                          >
                            <ListItemIcon>
                              <FileText size={24} />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="subtitle1" fontWeight={500}>
                                  {attachment.fileName}
                                </Typography>
                              }
                              secondary={
                                <Box display="flex" flexDirection="column" gap={0.5} component="div">
                                  <Typography variant="body2" color="text.secondary" component="span">
                                    {formatFileSize(attachment.fileSize)} • {formatDateTime(attachment.createdAt)}
                                  </Typography>
                                  {attachment.description && (
                                    <Typography variant="body2" color="text.secondary" component="span">
                                      {attachment.description}
                                    </Typography>
                                  )}
                                  <Box display="flex" gap={0.5} mt={0.5}>
                                    {attachment.fileUrl && (
                                      <>
                                        <IconButton
                                          size="small"
                                          onClick={async () => {
                                            setViewLoading(true);
                                            setViewFileName(attachment.fileName);
                                            setViewAttachment(attachment);
                                            try {
                                              const response = await api.get(
                                                `/api/leads/${leadId}/attachments/${attachment._id}/download`,
                                                { params: { businessId, inline: 1 }, responseType: 'blob' }
                                              );
                                              const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
                                              setViewFileUrl(blobUrl);
                                            } catch (err) {
                                              setViewFileUrl(null);
                                              setViewAttachment(null);
                                              alert(err.response?.data?.message || 'Failed to load file');
                                            } finally {
                                              setViewLoading(false);
                                            }
                                          }}
                                          title="View"
                                          color="primary"
                                        >
                                          <Eye size={18} />
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          onClick={async () => {
                                            try {
                                              const response = await api.get(
                                                `/api/leads/${leadId}/attachments/${attachment._id}/download`,
                                                { params: { businessId }, responseType: 'blob' }
                                              );
                                              const url = window.URL.createObjectURL(new Blob([response.data]));
                                              const link = document.createElement('a');
                                              link.href = url;
                                              link.setAttribute('download', attachment.fileName);
                                              document.body.appendChild(link);
                                              link.click();
                                              link.remove();
                                              window.URL.revokeObjectURL(url);
                                            } catch (error) {
                                              alert('Failed to download file');
                                            }
                                          }}
                                          title="Download"
                                        >
                                          <Download size={18} />
                                        </IconButton>
                                      </>
                                    )}
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteAttachment(attachment._id)}
                                      title="Delete"
                                      color="error"
                                    >
                                      <Trash size={18} />
                                    </IconButton>
                                  </Box>
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                      
                      {/* Storage Usage - Discreet at bottom */}
                      {storageStats && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                            <Typography variant="caption" color="text.secondary">
                              Storage: {formatFileSize(storageStats.totalSize)} used of {formatFileSize(storageStats.storageLimit)} ({Math.round(storageStats.storageUsedPercent)}%)
                            </Typography>
                          </Box>
                          <Box sx={{ width: '100%', height: 4, bgcolor: 'divider', borderRadius: 0.5, overflow: 'hidden' }}>
                            <Box
                              sx={{
                                height: '100%',
                                bgcolor: storageStats.storageUsedPercent > 90 ? 'error.main' : storageStats.storageUsedPercent > 75 ? 'warning.main' : 'primary.main',
                                width: `${Math.min(storageStats.storageUsedPercent, 100)}%`,
                                transition: 'width 0.3s ease'
                              }}
                            />
                          </Box>
                        </Box>
                      )}
                    </>
                  )}
                  {/* Upload Confirmation Dialog */}
                  <Dialog open={showUploadDialog} onClose={() => !uploadingFile && setShowUploadDialog(false)}>
                    <DialogTitle>Upload File</DialogTitle>
                    <DialogContent>
                      <Typography variant="body2" gutterBottom>
                        File: {selectedFile?.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Size: {selectedFile ? formatFileSize(selectedFile.size) : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Allowed types: PDF, Word (.doc, .docx), Excel (.xls, .xlsx), PowerPoint (.ppt, .pptx), Images (JPEG, PNG, GIF, WebP), Text files (.txt)
                      </Typography>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setShowUploadDialog(false)} disabled={uploadingFile}>
                        Cancel
                      </Button>
                      <Button onClick={handleFileUpload} variant="contained" disabled={uploadingFile || !selectedFile}>
                        {uploadingFile ? 'Uploading...' : 'Upload'}
                      </Button>
                    </DialogActions>
                  </Dialog>

                  {/* File View Dialog */}
                  <Dialog
                    open={!!viewFileUrl || viewLoading}
                    onClose={() => {
                      if (viewFileUrl && viewFileUrl.startsWith('blob:')) {
                        window.URL.revokeObjectURL(viewFileUrl);
                      }
                      setViewFileUrl(null);
                      setViewFileName('');
                      setViewAttachment(null);
                    }}
                    maxWidth="lg"
                    fullWidth
                  >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="h6">{viewFileName}</Typography>
                      <IconButton
                        onClick={() => {
                          setViewFileUrl(null);
                          setViewFileName('');
                        }}
                      >
                        <X size={24} />
                      </IconButton>
                    </DialogTitle>
                    <DialogContent>
                      <Box sx={{ height: '70vh', width: '100%' }}>
                        {viewLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Typography color="text.secondary">Loading...</Typography>
                          </Box>
                        ) : viewFileUrl && (
                          <>
                            {viewFileName.toLowerCase().endsWith('.pdf') || viewFileName.toLowerCase().includes('.pdf') ? (
                              <iframe
                                src={viewFileUrl}
                                width="100%"
                                height="100%"
                                style={{ border: 'none' }}
                                title="File Preview"
                              />
                            ) : viewFileName.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <img
                                  src={viewFileUrl}
                                  alt={viewFileName}
                                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                />
                              </Box>
                            ) : (
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                <FileText size={64} style={{ opacity: 0.5, marginBottom: 16 }} />
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                  Preview not available for this file type
                                </Typography>
                                <Button
                                  variant="contained"
                                  startIcon={<Download size={20} />}
                                  onClick={async () => {
                                    if (!viewAttachment) return;
                                    try {
                                      const response = await api.get(
                                        `/api/leads/${leadId}/attachments/${viewAttachment._id}/download`,
                                        { params: { businessId }, responseType: 'blob' }
                                      );
                                      const url = window.URL.createObjectURL(new Blob([response.data]));
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.setAttribute('download', viewFileName);
                                      document.body.appendChild(link);
                                      link.click();
                                      link.remove();
                                      window.URL.revokeObjectURL(url);
                                    } catch (error) {
                                      alert('Failed to download file');
                                    }
                                  }}
                                >
                                  Download File
                                </Button>
                              </Box>
                            )}
                          </>
                        )}
                      </Box>
                    </DialogContent>
                    <DialogActions>
                      <Button
                        onClick={() => {
                          if (viewFileUrl && viewFileUrl.startsWith('blob:')) {
                            window.URL.revokeObjectURL(viewFileUrl);
                          }
                          setViewFileUrl(null);
                          setViewFileName('');
                          setViewAttachment(null);
                        }}
                      >
                        Close
                      </Button>
                      {viewAttachment && (
                        <Button
                          variant="contained"
                          onClick={async () => {
                            try {
                              const response = await api.get(
                                `/api/leads/${leadId}/attachments/${viewAttachment._id}/download`,
                                { params: { businessId }, responseType: 'blob' }
                              );
                              const url = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', viewFileName);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              window.URL.revokeObjectURL(url);
                            } catch (error) {
                              alert('Failed to download file');
                            }
                          }}
                          startIcon={<Download size={20} />}
                        >
                          Download
                        </Button>
                      )}
                    </DialogActions>
                  </Dialog>
                </Box>
              </CardContent>
            </Card>
          </SupplierFeatureGate>

          {/* Reminders Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box>
                  <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems={isMobile ? 'stretch' : 'center'} gap={2} mb={2}>
                    <Typography variant="h6">
                      Reminders
                    </Typography>
                    <Button
                      startIcon={<Plus size={20} />}
                      variant="contained"
                      size="small"
                      onClick={() => {
                        setEditingReminder(null);
                        setReminderForm({ reminderDate: '', reminderTime: '', message: '' });
                        setShowReminderDialog(true);
                      }}
                    >
                      Set Reminder
                    </Button>
                  </Box>
                  {remindersLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                      <Typography>Loading reminders...</Typography>
                    </Box>
                  ) : reminders.length === 0 ? (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                      <Bell size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No reminders set
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        Set reminders to follow up with this couple at specific dates and times.
                      </Typography>
                    </Paper>
                  ) : (
                    <List>
                      {reminders
                        .sort((a, b) => new Date(a.reminderDate) - new Date(b.reminderDate))
                        .map((reminder) => {
                          const status = getReminderStatus(reminder);
                          return (
                            <ListItem
                              key={reminder._id}
                              sx={{
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                mb: 1,
                                alignItems: 'flex-start',
                                '&:hover': {
                                  bgcolor: 'action.hover'
                                }
                              }}
                            >
                              <ListItemIcon>
                                {!reminder.isCompleted ? (
                                  <IconButton
                                    onClick={() => {
                                      setReminderToComplete(reminder);
                                      setShowCompleteReminderModal(true);
                                    }}
                                    title="Mark as Complete"
                                    color="success"
                                    size="small"
                                  >
                                    <CheckCircle size={24} />
                                  </IconButton>
                                ) : (
                                  <CheckCircle size={24} color="success" />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Box display="flex" alignItems="center" gap={1} component="div">
                                    <Typography variant="subtitle1" fontWeight={500} component="span">
                                      {reminder.message}
                                    </Typography>
                                    <Chip
                                      label={status.label}
                                      size="small"
                                      color={status.color}
                                    />
                                  </Box>
                                }
                                secondary={
                                  <Box component="div">
                                    <Typography variant="body2" color="text.secondary" component="span">
                                      Follow-up on {formatDateTime(reminder.reminderDate)}
                                    </Typography>
                                    {isMobile && (
                                      <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                                        {!reminder.isCompleted && (
                                          <IconButton
                                            size="small"
                                            onClick={() => handleEditReminder(reminder)}
                                            title="Edit Reminder"
                                            color="primary"
                                          >
                                            <Clock size={20} />
                                          </IconButton>
                                        )}
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            setReminderToDelete(reminder);
                                            setShowDeleteReminderModal(true);
                                          }}
                                          title="Delete"
                                          color="error"
                                        >
                                          <Trash size={20} />
                                        </IconButton>
                                      </Box>
                                    )}
                                  </Box>
                                }
                              />
                              {!isMobile && (
                                <ListItemSecondaryAction>
                                  <Box display="flex" gap={1}>
                                    {!reminder.isCompleted && (
                                      <IconButton
                                        edge="end"
                                        onClick={() => handleEditReminder(reminder)}
                                        title="Edit Reminder"
                                        color="primary"
                                      >
                                        <Clock size={20} />
                                      </IconButton>
                                    )}
                                    <IconButton
                                      edge="end"
                                      onClick={() => {
                                        setReminderToDelete(reminder);
                                        setShowDeleteReminderModal(true);
                                      }}
                                      title="Delete"
                                      color="error"
                                    >
                                      <Trash size={20} />
                                    </IconButton>
                                  </Box>
                                </ListItemSecondaryAction>
                              )}
                            </ListItem>
                          );
                        })}
                    </List>
                  )}
                  {/* Complete Reminder Confirmation */}
                  <Dialog
                    open={showCompleteReminderModal}
                    onClose={() => {
                      setShowCompleteReminderModal(false);
                      setReminderToComplete(null);
                    }}
                  >
                    <DialogTitle>Complete Reminder</DialogTitle>
                    <DialogContent>
                      <Typography>
                        Mark this reminder as complete?
                        {reminderToComplete && (
                          <Box component="span" display="block" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                            "{reminderToComplete.message}"
                          </Box>
                        )}
                      </Typography>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => { setShowCompleteReminderModal(false); setReminderToComplete(null); }}>
                        Cancel
                      </Button>
                      <Button variant="contained" color="success" onClick={() => reminderToComplete && handleCompleteReminder(reminderToComplete._id)}>
                        Complete
                      </Button>
                    </DialogActions>
                  </Dialog>

                  {/* Delete Reminder Confirmation */}
                  <Dialog
                    open={showDeleteReminderModal}
                    onClose={() => {
                      setShowDeleteReminderModal(false);
                      setReminderToDelete(null);
                    }}
                  >
                    <DialogTitle>Delete Reminder</DialogTitle>
                    <DialogContent>
                      <Typography>
                        Are you sure you want to delete this reminder?
                        {reminderToDelete && (
                          <Box component="span" display="block" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                            "{reminderToDelete.message}"
                          </Box>
                        )}
                      </Typography>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => { setShowDeleteReminderModal(false); setReminderToDelete(null); }}>
                        Cancel
                      </Button>
                      <Button variant="contained" color="error" onClick={() => reminderToDelete && handleDeleteReminder(reminderToDelete._id)}>
                        Delete
                      </Button>
                    </DialogActions>
                  </Dialog>

                  {/* Set/Edit Reminder Dialog */}
                  <Dialog 
                    open={showReminderDialog} 
                    onClose={() => {
                      setShowReminderDialog(false);
                      setEditingReminder(null);
                      setReminderForm({ reminderDate: '', reminderTime: '', message: '' });
                    }} 
                    maxWidth="sm" 
                    fullWidth
                  >
                    <DialogTitle>{editingReminder ? 'Edit Reminder' : 'Set Reminder'}</DialogTitle>
                    <DialogContent>
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Date"
                            type="date"
                            value={reminderForm.reminderDate}
                            onChange={(e) => setReminderForm(prev => ({ ...prev, reminderDate: e.target.value }))}
                            InputLabelProps={{
                              shrink: true,
                            }}
                            inputProps={{
                              min: new Date().toISOString().split('T')[0]
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Time"
                            type="time"
                            value={reminderForm.reminderTime}
                            onChange={(e) => setReminderForm(prev => ({ ...prev, reminderTime: e.target.value }))}
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Message"
                            multiline
                            rows={3}
                            value={reminderForm.message}
                            onChange={(e) => setReminderForm(prev => ({ ...prev, message: e.target.value }))}
                            placeholder="Enter reminder message..."
                          />
                        </Grid>
                      </Grid>
                    </DialogContent>
                    <DialogActions>
                      <Button 
                        onClick={() => {
                          setShowReminderDialog(false);
                          setEditingReminder(null);
                          setReminderForm({ reminderDate: '', reminderTime: '', message: '' });
                        }}
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleCreateReminder} 
                        variant="contained"
                        disabled={!reminderForm.reminderDate || !reminderForm.reminderTime || !reminderForm.message}
                      >
                        {editingReminder ? 'Update Reminder' : 'Set Reminder'}
                      </Button>
                    </DialogActions>
                  </Dialog>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Archive button at bottom on mobile */}
      {isMobile && (
        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          {(lead.stageSlug === 'archived' || lead.stage === 'archived' || lead.status === 'archived') ? (
            <Button
              fullWidth
              variant="contained"
              startIcon={<Archive size={20} />}
              onClick={handleUnarchiveLead}
              disabled={saving}
              sx={{ bgcolor: '#ef4192', color: '#fff', '&:hover': { bgcolor: '#d63681' } }}
            >
              Unarchive
            </Button>
          ) : (
            <Button
              fullWidth
              variant="contained"
              startIcon={<Archive size={20} />}
              onClick={handleArchiveLead}
              disabled={saving}
              sx={{ bgcolor: '#ef4192', color: '#fff', '&:hover': { bgcolor: '#d63681' } }}
            >
              Archive
            </Button>
          )}
        </Box>
      )}
      {/* Event Form Modal */}
      {showEventForm && (
        <EventForm
          event={editingEvent}
          leadId={lead?._id}
          autoLinkLead={!!lead?._id}
          onSuccess={() => {
            setShowEventForm(false);
            setEditingEvent(null);
            fetchLead();
            fetchDateWarnings();
          }}
          onClose={() => {
            setShowEventForm(false);
            setEditingEvent(null);
          }}
        />
      )}
      <FormSubmissionEntryDialog
        open={Boolean(submissionDialogEntry)}
        onClose={() => {
          setSubmissionDialogEntry(null);
          setSubmissionDialogForm(null);
        }}
        entry={submissionDialogEntry}
        form={submissionDialogForm}
      />
      {/* Link Wedding Modal */}
      <Dialog open={showLinkWeddingModal} onClose={() => setShowLinkWeddingModal(false)}>
        <DialogTitle>Link Wedding</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Wedding Code"
            fullWidth
            variant="outlined"
            value={weddingCode}
            onChange={(e) => setWeddingCode(e.target.value)}
            placeholder="Enter 6-digit wedding code"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLinkWeddingModal(false)}>Cancel</Button>
          <Button onClick={handleLinkWedding} variant="contained">
            Link Wedding
          </Button>
        </DialogActions>
      </Dialog>
      {/* Edit details modal */}
      <Dialog open={showEditModal} onClose={() => setShowEditModal(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit details</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name"
                fullWidth
                value={editData.name}
                onChange={(e) => handleEditChange('name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                fullWidth
                value={editData.email}
                onChange={(e) => handleEditChange('email', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                fullWidth
                value={editData.phone}
                onChange={(e) => handleEditChange('phone', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Wedding Date"
                type="date"
                fullWidth
                value={editData.weddingDate}
                onChange={(e) => handleEditChange('weddingDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Source"
                select
                fullWidth
                value={editData.leadSource}
                onChange={(e) => handleEditChange('leadSource', e.target.value)}
              >
                {mergeLeadSources([
                  ...leadSourceOptions,
                  ...(editData?.leadSource &&
                  !leadSourceOptions.some((source) => source.value === editData.leadSource)
                    ? [{ value: editData.leadSource, label: getLeadSourceLabel(editData.leadSource, DEFAULT_CRM_LEAD_SOURCES) }]
                    : [])
                ]).map((source) => (
                  <MenuItem key={source.value} value={source.value}>
                    {source.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {editData.leadSource === 'other' && (
              <Grid item xs={12}>
                <TextField
                  label="Where did they come from?"
                  fullWidth
                  value={editData.leadSourceOther}
                  onChange={(e) => handleEditChange('leadSourceOther', e.target.value)}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Min Budget"
                type="number"
                fullWidth
                value={editData.budgetRange.min}
                onChange={(e) => handleEditChange('budgetRange', { ...editData.budgetRange, min: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Max Budget"
                type="number"
                fullWidth
                value={editData.budgetRange.max}
                onChange={(e) => handleEditChange('budgetRange', { ...editData.budgetRange, max: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Priority"
                select
                fullWidth
                value={editData.priority}
                onChange={(e) => handleEditChange('priority', e.target.value)}
              >
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="low">Low</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Stage"
                select
                fullWidth
                value={editData.stage}
                onChange={(e) => handleEditChange('stage', e.target.value)}
              >
                {(stageOptions || []).map(stage => (
                  <MenuItem key={stage.slug} value={stage.slug}>
                    {stage.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Assigned to"
                select
                fullWidth
                value={editData.assignedTo || ''}
                onChange={(e) => handleEditChange('assignedTo', e.target.value)}
              >
                <MenuItem value="">
                  <em>Unassigned</em>
                </MenuItem>
                {teamMembers.map((member) => (
                  <MenuItem key={member._id} value={member._id}>
                    {formatMemberLabel(member)}
                  </MenuItem>
                ))}
              </TextField>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Edit in the{' '}
                <RouterLink to="/supplier/people" style={{ color: 'inherit', fontWeight: 600 }}>
                  Team
                </RouterLink>
                {' '}area.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Tags
              </Typography>
              <Box display="flex" gap={1} mb={1.5}>
                <TextField
                  label="Add tag"
                  size="small"
                  fullWidth
                  value={newEditTag}
                  onChange={(e) => setNewEditTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddEditTag();
                    }
                  }}
                />
                <Button onClick={() => handleAddEditTag()} disabled={!String(newEditTag || '').trim()}>
                  Add
                </Button>
              </Box>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {(editData.tags || []).map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    onDelete={() => handleRemoveEditTag(tag)}
                    size="small"
                    icon={<Tag size={14} />}
                  />
                ))}
              </Stack>
              {!!availableTags.length && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 1 }}>
                    Existing tags
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {availableTags
                      .filter((tag) => !(editData.tags || []).includes(tag))
                      .map((tag) => (
                        <Chip
                          key={`edit-suggestion-${tag}`}
                          label={tag}
                          size="small"
                          variant="outlined"
                          onClick={() => handleAddEditTag(tag)}
                          clickable
                        />
                      ))}
                  </Stack>
                </>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Wedding Code"
                fullWidth
                value={editData.weddingCode}
                onChange={(e) => handleEditChange('weddingCode', e.target.value)}
                placeholder="6-digit code"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {lead?.wedding && (
            <Button
              color="error"
              variant="text"
              onClick={() => handleEditChange('unlinkWedding', true)}
            >
              Unlink Wedding
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button onClick={handleSaveLead} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(declineBookingId)} onClose={() => !declineBusy && setDeclineBookingId(null)}>
        <DialogTitle>Decline this booking?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The couple will be notified that this appointment request was declined.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineBookingId(null)} disabled={declineBusy}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleLeadDeclineBooking} disabled={declineBusy}>
            {declineBusy ? 'Declining…' : 'Decline booking'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadDetailView;
